import { createSharedComposable } from '@vueuse/core';
import { ref, computed } from 'vue';
import { injectService } from '@patsnap/tahiti-core/ioc/inject';
import { MaterialTechQaApi } from '@/domains/material/apis/techqa-api';
import { useTimeoutTimer } from '@/composables/utils/use-timeout';
import type {
  DETAIL_MODE,
  ConversationData,
  ConversationMessageData,
  FileQuery,
  ParsedSource,
  RetrievalResult,
  TECH_QA_STAGE,
  TECH_QA_STATUS,
  TechQaStreamData,
} from '@/types/tech-qa/tech-qa';
import { useRoute, useRouter } from 'vue-router';
import type { Router } from 'vue-router';
import { useUpload } from './use-upload';
import { useLoginSdk } from '../use-loginsdk';
import { i18n } from '@/infrastructure/locales';
import { useLimitReach } from '../utils/use-limit-reach';
import { useTracking } from '../tracking/use-tracking';
import { EXAMPLE_LINKS } from './example-links';
import { REGION } from '@eureka/utils/environment';

const { openLoginDialog } = useLoginSdk(i18n as any);
const { sendTracking } = useTracking();

export const useConversation = createSharedComposable(() => {
  const isLoadingConversation = ref(false);
  const conversationData = ref<ConversationData>();
  const materialTechQaApiService = injectService<MaterialTechQaApi>(MaterialTechQaApi);
  const openedMessageId = ref('');
  const messages = ref(0);
  const pipelineRunning = ref(false);
  const isCreatingConversation = ref(false);
  const isSendingEmail = ref(false);

  // IMAGE UPLOAD
  const { fileInput, clearAllUploads } = useUpload();

  const STAGE_WITH_STREAMING_OUTPUT = new Set<TECH_QA_STAGE>(['SUMMARISATION']);
  const STAGE_WITH_MARKDOWN_THINKING = new Set<TECH_QA_STAGE>([
    'QUERY_UNDERSTANDING',
    'RETRIEVAL',
    'SUMMARISATION',
  ]);
  const STAGE_WITH_STRING_OUTPUT = new Set<TECH_QA_STAGE>(['SUMMARISATION']); // results: { content: string }
  const STAGE_WITH_JSON_OUTPUT = new Set<TECH_QA_STAGE>(['QUERY_UNDERSTANDING', 'RETRIEVAL']); // results: { content: JSON_STRING }

  const router = useRouter();
  const route = useRoute();

  const { ifLimitReachDialog } = useLimitReach();

  const detailMode = ref<DETAIL_MODE>('RESULTS');

  const timeOutCallback = (reject: (e: Error) => void) => {
    const lastMessage = conversationData.value?.messages.at(-1); // assistant message

    if (lastMessage) {
      lastMessage.status = 'FE_TIMEOUT';
    }

    reject(new Error('FE_TIMEOUT'));
  };

  const setOpenMessage = (messageId: string, mode: DETAIL_MODE = 'RESULTS') => {
    detailMode.value = mode;
    openedMessageId.value = messageId;
  };

  const ifLastMessageIsFast = computed(() => {
    try {
      return (
        JSON.parse(conversationData.value?.messages.at(-1)?.results || '').content_type ===
        'fast_result'
      );
    } catch {
      return false;
    }
  });

  const openedMessage = computed(() => {
    return conversationData.value?.messages.find((m) => {
      return m.message_id === openedMessageId.value;
    });
  });

  const ifMessageOpened = computed(() => !!openedMessage.value);

  const scrollToLastQuery = () => {
    // Find all elements with the class
    const bubbles = document.querySelectorAll('.user-input-bubble');

    // Get the last one
    const lastBubble = bubbles[bubbles.length - 1];

    if (lastBubble) {
      // Smooth scroll and align it to the top of the viewport
      lastBubble.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  const createUserMessage = (input: string, stage: TECH_QA_STAGE, fileQuery: FileQuery[]) => {
    const conversationId = conversationData.value?.conversation_id || '';
    messages.value = messages.value + 1;

    conversationData.value?.messages.push({
      message_id: `msg-${messages.value}`,
      user_id: '',
      conversation_id: conversationId,
      role: 'user',
      input,
      thinking: '',
      results: '',
      stage,
      file_query: fileQuery,
      status: 'PROCESSING',
    });

    setTimeout(scrollToLastQuery, 100);
  };

  const createEmptyAssistantMessage = (stage: TECH_QA_STAGE) => {
    const conversationId = conversationData.value?.conversation_id || '';
    messages.value = messages.value + 1;

    conversationData.value?.messages.push({
      message_id: `msg-${messages.value}`,
      user_id: '',
      conversation_id: conversationId,
      role: 'assistant',
      input: '',
      thinking: '',
      results: '',
      stage,
      status: 'PROCESSING',
    });
  };

  const createNewThinking = (accumulatedThinking: string, status: TECH_QA_STATUS) => {
    return JSON.stringify({
      content: accumulatedThinking,
      content_type: 'thinking',
      status,
    });
  };

  const createNewResults = (accumulatedResults: string, status: TECH_QA_STATUS) => {
    return JSON.stringify({
      content: accumulatedResults,
      content_type: 'results',
      status,
    });
  };

  const createNewFastResult = (accumulatedResults: string, status: TECH_QA_STATUS) => {
    return JSON.stringify({
      content: accumulatedResults,
      content_type: 'fast_result',
      status,
    });
  };

  const getParsedResults = (message: ConversationMessageData) => {
    try {
      if (STAGE_WITH_STRING_OUTPUT.has(message.stage)) {
        return JSON.parse(message.results || '')?.content ?? '';
      } else if (STAGE_WITH_JSON_OUTPUT.has(message.stage)) {
        return JSON.parse(JSON.parse(message.results || '')?.content);
      }
      return message.results;
    } catch {
      if (message.stage === 'SUMMARISATION') {
        return '';
      }

      return {};
    }
  };

  const summarisationPipeline = (conversation_id: string) => {
    pipelineRunning.value = true;

    const stream = materialTechQaApiService.getSummarisationStream({
      conversation_id,
    });

    // add messages
    createEmptyAssistantMessage('SUMMARISATION');

    stream.start();

    const accumulated = {
      thinking: '',
      results: '',
    };

    return new Promise<void>((resolve, reject) => {
      const { clear, start, refresh } = useTimeoutTimer();

      start(() => {
        timeOutCallback(reject);
      });
      stream.onMessage((m) => {
        const message = m as any as TechQaStreamData;
        const lastMessage = conversationData.value?.messages.at(-1); // assistant message

        if (message.content_type === 'error') {
          timeOutCallback(reject);
        }

        if (message.content_type !== 'heartbeat') {
          refresh();
        }

        if (message.content_type === 'thinking' || message.content_type === 'results') {
          if (message.status === 'PROCESSING') {
            accumulated[message.content_type] += message.content;
          } else {
            accumulated[message.content_type] = message.content;
          }
        }

        if (!lastMessage) {
          return;
        }

        if (message.status === 'STREAM_END') {
          clear();
          lastMessage.status = 'COMPLETED';
          pipelineRunning.value = false;
          resolve();
        } else {
          if (message.content_type === 'thinking') {
            const newThinking = createNewThinking(accumulated.thinking, message.status);
            lastMessage.thinking = newThinking;
          } else if (message.content_type === 'results') {
            const newResults = createNewResults(accumulated.results, message.status);
            lastMessage.results = newResults;
          } else if (message.content_type === 'sources') {
            lastMessage.sources = JSON.parse(message.content);
          } else if (message.content_type === 'suggested_qns') {
            lastMessage.suggested_qns = JSON.parse(message.content);
          }
        }
      });
    });
  };

  const retrievalPipeline = (conversation_id: string, keywords: Record<any, any>) => {
    pipelineRunning.value = true;

    const stream = materialTechQaApiService.getRetrievalStream({
      ...keywords,
      conversation_id,
    });

    // add messages
    createEmptyAssistantMessage('RETRIEVAL');

    stream.start();

    const accumulated = {
      thinking: '',
      results: '',
    };

    return new Promise<void>((resolve, reject) => {
      const { clear, start, refresh } = useTimeoutTimer();

      start(() => {
        timeOutCallback(reject);
      });

      stream.onMessage((m) => {
        const message = m as any as TechQaStreamData;
        const lastMessage = conversationData.value?.messages.at(-1); // assistant message

        if (message.content_type === 'error') {
          timeOutCallback(reject);
        }

        if (message.content_type !== 'heartbeat') {
          refresh();
        }

        if (message.content_type === 'thinking' || message.content_type === 'results') {
          if (message.status === 'PROCESSING') {
            accumulated[message.content_type] += message.content;
          } else {
            accumulated[message.content_type] = message.content;
          }
        }

        if (!lastMessage) {
          return;
        }

        if (message.status === 'STREAM_END') {
          clear();
          lastMessage.status = 'COMPLETED';

          // Version 1: Automatically goes to next step
          if (conversationData.value?.conversation_id) {
            pipelineRunning.value = false;
          }
          resolve();
        } else {
          const newThinking = createNewThinking(accumulated.thinking, message.status);
          const newResults = createNewResults(accumulated.results, message.status);
          lastMessage.thinking = newThinking;
          lastMessage.results = newResults;
        }
      });
    });
  };

  const queryUnderstandingPipeline = async (
    conversation_id: string,
    query: string,
    file_query: FileQuery[],
  ) => {
    pipelineRunning.value = true;

    const stream = materialTechQaApiService.getQueryUnderstandingStream({
      query: decodeURIComponent(query),
      conversation_id,
      file_query,
    });

    clearAllUploads();

    // add messages
    createUserMessage(decodeURIComponent(query), 'QUERY_UNDERSTANDING', file_query);
    createEmptyAssistantMessage('QUERY_UNDERSTANDING');

    stream.start();

    const accumulated = {
      thinking: '',
      results: '',
      fast_result: '',
    };

    return new Promise<void>((resolve, reject) => {
      const { clear, start, refresh } = useTimeoutTimer();

      start(() => {
        timeOutCallback(reject);
      });

      stream.onMessage((m) => {
        const message = m as any as TechQaStreamData;
        const lastMessage = conversationData.value?.messages.at(-1); // assistant message

        if (message.content_type === 'error') {
          timeOutCallback(reject);
        }

        if (message.content_type !== 'heartbeat') {
          refresh();
        }

        if (
          message.content_type === 'thinking' ||
          message.content_type === 'results' ||
          message.content_type === 'fast_result'
        ) {
          if (message.status === 'PROCESSING') {
            accumulated[message.content_type] += message.content;
          } else {
            accumulated[message.content_type] = message.content;
          }
        }

        if (!lastMessage) {
          return;
        }

        if (message.status === 'STREAM_END') {
          clear();
          lastMessage.status = 'COMPLETED';

          // Version 1: Automatically goes to next step
          if (conversationData.value?.conversation_id) {
            pipelineRunning.value = false;
          }
          resolve();
        } else {
          const newThinking = createNewThinking(accumulated.thinking, message.status);
          const newResults = createNewResults(accumulated.results, message.status);
          lastMessage.thinking = newThinking;
          lastMessage.results = newResults;

          if (accumulated.fast_result !== '') {
            lastMessage.results = createNewFastResult(accumulated.fast_result, message.status);
          }
        }
      });
    });
  };

  const shareConversation = async () => {
    if (conversationData.value?.conversation_id) {
      await materialTechQaApiService.handleShare(conversationData.value.conversation_id);
    }
  };

  const conversationId = computed(() => {
    return conversationData.value?.conversation_id || '';
  });

  const fullPipeline = async (query: string) => {
    if (!conversationId.value) return;

    await queryUnderstandingPipeline(conversationId.value, query, fileInput.value);

    const lastMessage = conversationData.value?.messages.at(-1);
    if (!lastMessage) return;

    if (!ifLastMessageIsFast.value) {
      await retrievalPipeline(conversationId.value, getParsedResults(lastMessage));
      await summarisationPipeline(conversationId.value);
    }
  };

  const recover = async (conversationId: string) => {
    const lastMessage = conversationData.value?.messages.at(-1);

    if (!lastMessage) {
      return false;
    }

    if (lastMessage.status === 'PROCESSING') {
      setTimeout(async () => {
        conversationData.value = await materialTechQaApiService.getConversation(conversationId);
        recover(conversationId);
      }, 1000);
    }

    if (lastMessage.stage === 'QUERY_UNDERSTANDING' && lastMessage.status === 'COMPLETED') {
      if (!ifLastMessageIsFast.value) {
        await retrievalPipeline(conversationId, getParsedResults(lastMessage));
        await summarisationPipeline(conversationId);
      }
    }

    if (lastMessage.stage === 'RETRIEVAL' && lastMessage.status === 'COMPLETED') {
      await summarisationPipeline(conversationId);
    }
  };

  const getConversation = async (conversationId: string) => {
    isLoadingConversation.value = true;

    try {
      conversationData.value = await materialTechQaApiService.getConversation(conversationId);

      // for edge case (recovery)
      if (!pipelineRunning.value) {
        recover(conversationId);
      }

      if (conversationData.value.messages.length === 0 && route.query.query) {
        fullPipeline(route.query.query as string);
      }

      isLoadingConversation.value = false;
    } catch {
      router.push({ name: 'MainHomePage' });
    }
  };

  const guestLoginLimitDialog = () => {
    sendTracking('ss_plg_register', 'register_exposure', {
      registrationSource: 'mse_techqa',
    });
    openLoginDialog(false, true, () => {
      sendTracking('ss_plg_register', 'register_close', {
        registrationSource: 'mse_techqa',
      });
    });
  };

  /**
   * Creates a conversation and navigates to it.
   * Pass `router` when calling from a context where the composable's router may be undefined (e.g. shared composable from home).
   */
  const createConversation = async (
    query: string,
    routerInstance?: Router,
  ): Promise<string | null> => {
    isCreatingConversation.value = true;
    try {
      const data = await materialTechQaApiService.createConversation(query);
      const conversationId = data?.conversation_id ?? null;
      if (conversationId) {
        sendTracking('mse_tech_qa_create_conversation', 'agent_home');
        const r = routerInstance ?? router;
        if (r) {
          await r.push({
            name: 'TechQaConversation',
            params: { conversation_id: conversationId },
            query: { query: encodeURIComponent(query) },
          });
        }
      }
      return conversationId;
    } catch (e) {
      if ((e as any)?.cause?.data?.error_code === 'errors.com.patsnap.acl_limit_exceed') {
        ifLimitReachDialog.value = true;
      }
      if ((e as any).cause.error_code === 90180009) {
        // Guest Limit Exceeded
        guestLoginLimitDialog();
      }
      return '';
    } finally {
      isCreatingConversation.value = false;
    }
  };

  const sourceList = computed<ParsedSource>(() => {
    try {
      if (!conversationData.value?.messages) {
        return {
          patent: {},
          paper: {},
          website: {},
        };
      }

      const retrievalMessages = conversationData.value.messages.filter((m) => {
        return m.stage === 'RETRIEVAL' && m.status === 'COMPLETED' && m.role === 'assistant';
      });

      const retrievalResults: RetrievalResult[] = retrievalMessages.map((m) => {
        try {
          return getParsedResults(m) as RetrievalResult;
        } catch {
          return {
            patent: { count: 0, sources: [] },
            paper: { count: 0, sources: [] },
            website: { count: 0, sources: [] },
          };
        }
      });

      const res = retrievalResults.reduce(
        (p, c) => {
          c.patent.sources.forEach((s) => {
            p.patent[s.id] = s;
          });

          c.paper.sources.forEach((s) => {
            p.paper[s.id] = s;
          });

          c.website.sources.forEach((s) => {
            p.website[s.id] = s;
          });

          return p;
        },
        {
          patent: {},
          paper: {},
          website: {},
        } as ParsedSource,
      );

      return res;
    } catch {
      return {
        patent: {},
        paper: {},
        website: {},
      };
    }
  });

  const sendSharingEmail = async (targetEmail: string) => {
    isSendingEmail.value = true;
    await materialTechQaApiService.shareEmail(conversationId.value, targetEmail);
    isSendingEmail.value = false;
  };

  const goToExample = (index: number) => {
    const currentLanguage = i18n.global.locale.value;
    const link = EXAMPLE_LINKS[REGION]?.[currentLanguage]?.[index];

    if (link) {
      window.location.href = link;
    }
  };

  return {
    STAGE_WITH_STREAMING_OUTPUT,
    STAGE_WITH_MARKDOWN_THINKING,
    ifMessageOpened,
    conversationData,
    getConversation,
    setOpenMessage,
    openedMessageId,
    openedMessage,
    getParsedResults,
    fullPipeline,
    pipelineRunning,
    isLoadingConversation,
    scrollToLastQuery,
    shareConversation,
    ifLastMessageIsFast,
    createConversation,
    isCreatingConversation,
    sourceList,
    isSendingEmail,
    sendSharingEmail,
    detailMode,
    goToExample,
  };
});
